import { useState } from "react";
import { featuredJobs, jobCategories } from "@/mocks/jobs";

export default function JobPostSection() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All"
    ? featuredJobs
    : featuredJobs.filter((j) => j.industry === activeCategory);

  return (
    <section id="jobs" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Latest Openings</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Job Post Board</h2>
          </div>
          <a href="#" className="text-sm font-medium text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap">
            View All Jobs &rarr;
          </a>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {["All", ...jobCategories.map((c) => c.name)].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs font-medium px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-500 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-amber-50 hover:text-amber-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job Listings */}
          <div className="lg:col-span-2 space-y-4">
            {(filtered.length > 0 ? filtered : featuredJobs).map((job) => (
              <div
                key={job.id}
                className="bg-white border border-stone-100 rounded-xl p-5 hover:border-amber-200 hover:shadow-sm transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl overflow-hidden bg-stone-50 flex-shrink-0">
                    <img src={job.logo} alt={job.company} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-base group-hover:text-amber-600 transition-colors">{job.title}</h3>
                        <p className="text-stone-500 text-sm">{job.company}</p>
                      </div>
                      <button className="w-8 h-8 flex items-center justify-center text-stone-300 hover:text-amber-500 transition-colors cursor-pointer flex-shrink-0">
                        <i className="ri-bookmark-line text-lg"></i>
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-stone-500">
                        <i className="ri-map-pin-line"></i> {job.location}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-stone-500">
                        <i className="ri-money-dollar-circle-line"></i> {job.salary}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        job.type === "Remote" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"
                      }`}>
                        {job.type}
                      </span>
                      {job.urgent && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-600">
                          Urgent
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-stone-400">{job.posted}</span>
                      <button className="text-xs font-semibold px-4 py-1.5 rounded-lg border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                        Quick Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Job Categories Sidebar */}
          <div className="space-y-4">
            <div className="bg-stone-50 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm">Browse by Industry</h3>
              <div className="space-y-2">
                {jobCategories.map((cat) => (
                  <div
                    key={cat.name}
                    className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-amber-50 cursor-pointer transition-colors group"
                    onClick={() => setActiveCategory(cat.name)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 group-hover:bg-amber-100">
                        <i className={`${cat.icon} text-amber-600 text-sm`}></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{cat.name}</span>
                    </div>
                    <span className="text-xs text-stone-400 font-medium">{cat.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Resume Upload CTA */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white">
              <i className="ri-file-user-line text-3xl mb-3 block"></i>
              <h3 className="font-bold text-base mb-1">Upload Your Resume</h3>
              <p className="text-white/80 text-xs mb-4">Let employers find you. 3,500+ companies actively recruiting.</p>
              <button className="w-full bg-white text-amber-600 font-semibold text-sm py-2.5 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
                Upload Resume
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
